/**
 * ModuleCoverageMatrix — per-module coverage score, sortable, with bulk stub.
 *
 * Extracted from AiUsageDashboard.tsx:746-914. Two corrections applied on
 * extraction:
 *   1. the bulk-stub window.confirm becomes a ConfirmModal (the platform
 *      forbids native dialogs; the old call survived only because nobody
 *      rewrote this file);
 *   2. the sort <select> becomes a SearchableDropdown.
 */
import React, { useState } from 'react';
import { Network } from 'lucide-react';
import ConfirmModal from '../../../components/credits/ConfirmModal';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { generateStubsBulk, type ToolCatalogModule } from '../../../services/aiControlApi';

type SortKey = 'label' | 'coverage' | 'endpoints';

interface Props {
  modules: ToolCatalogModule[];
  onRefetch: () => Promise<void> | void;
}

const ModuleRow: React.FC<{
  module: ToolCatalogModule;
  isBulkStubbing: boolean;
  onBulkStub: () => void;
}> = ({ module: m, isBulkStubbing, onBulkStub }) => {
  const config: Record<ToolCatalogModule['status'], { barColor: string; textColor: string; label: string }> = {
    full:    { barColor: 'bg-emerald-500',  textColor: 'text-emerald-600', label: 'Full' },
    partial: { barColor: 'bg-amber-400',    textColor: 'text-amber-600',   label: 'Partial' },
    stub:    { barColor: 'bg-amber-300',    textColor: 'text-amber-500',   label: 'Stub' },
    none:    { barColor: 'bg-rose-300',     textColor: 'text-rose-600',    label: 'Not covered' },
    skipped: { barColor: 'bg-stone-300',    textColor: 'text-gray-400',    label: 'Meta' },
  };
  const cfg = config[m.status];
  const pct = Math.max(0, Math.min(100, m.coveragePct));
  const canBulkCover = m.status !== 'skipped' && m.status !== 'full' && m.endpointCount > 0;

  return (
    <tr className="hover:bg-stone-50">
      <td className="px-3 py-2">
        <div className="text-sm font-medium text-gray-900">{m.label}</div>
        <div className="text-[11px] text-gray-500">
          {m.domains.length === 0 ? 'no backend domains' : m.domains.join(', ')}
        </div>
      </td>
      <td className="px-3 py-2 text-center text-xs text-gray-700">
        <span className={m.toolCount > 0 ? cfg.textColor + ' font-semibold' : 'text-rose-500 font-semibold'}>
          {m.toolCount}
        </span>
      </td>
      <td className="px-3 py-2 text-center text-xs text-gray-700">{m.endpointCount}</td>
      <td className="px-3 py-2">
        <div className="h-2 w-28 bg-stone-100 rounded-full overflow-hidden">
          <div className={`h-full ${cfg.barColor}`} style={{ width: `${pct}%` }} />
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        {m.status === 'skipped' ? (
          <span className="text-[10px] uppercase tracking-wide text-gray-400">meta</span>
        ) : (
          <span className={`text-sm font-semibold ${cfg.textColor}`}>
            {m.endpointCount === 0 ? '—' : `${pct}%`}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {canBulkCover ? (
          <button
            type="button"
            onClick={onBulkStub}
            disabled={isBulkStubbing}
            className="min-h-[44px] px-2 text-[11px] text-emerald-600 hover:text-emerald-700 hover:underline font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {isBulkStubbing ? 'Stubbing…' : 'Cover all GETs'}
          </button>
        ) : (
          <span className="text-[10px] text-gray-300">—</span>
        )}
      </td>
    </tr>
  );
};

const ModuleCoverageMatrix: React.FC<Props> = ({ modules, onRefetch }) => {
  const [sortBy, setSortBy] = useState<SortKey>('coverage');
  const [showSkipped, setShowSkipped] = useState(false);
  const [bulkStubbing, setBulkStubbing] = useState<string | null>(null);
  const [bulkResult, setBulkResult] = useState<string | null>(null);
  const [pendingBulk, setPendingBulk] = useState<ToolCatalogModule | null>(null);

  const runBulkStub = async (m: ToolCatalogModule) => {
    setPendingBulk(null);
    if (m.domains.length === 0) return;
    setBulkResult(null);
    setBulkStubbing(m.id);
    try {
      const res = await generateStubsBulk({ domains: m.domains });
      setBulkResult(`${m.label}: created ${res.created}, skipped ${res.skipped}, failed ${res.failed}`);
      await onRefetch();
    } catch (err: any) {
      setBulkResult(`${m.label}: failed — ${err?.message || 'unknown error'}`);
    } finally {
      setBulkStubbing(null);
    }
  };

  const filtered = modules.filter((m) => showSkipped || m.status !== 'skipped');

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'label') return a.label.localeCompare(b.label);
    if (sortBy === 'endpoints') return b.endpointCount - a.endpointCount;
    // coverage desc, but keep "none" at bottom
    if (a.status === 'none' && b.status !== 'none') return 1;
    if (b.status === 'none' && a.status !== 'none') return -1;
    return b.coveragePct - a.coveragePct;
  });

  const coveredCount = modules.filter((m) => m.status === 'full' || m.status === 'partial').length;
  const scorableTotal = modules.filter((m) => m.status !== 'skipped').length;

  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Network size={14} className="text-rose-600" />
            Module Coverage Matrix
          </h2>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {coveredCount}/{scorableTotal} modules have at least partial coverage.
            Score = tools / endpoints within the module's domains.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1 min-h-[44px] text-[11px] text-gray-600">
            <input
              type="checkbox"
              checked={showSkipped}
              onChange={(e) => setShowSkipped(e.target.checked)}
              className="h-4 w-4"
            />
            Show meta modules
          </label>
          <div className="w-48">
            <SearchableDropdown
              options={[
                { value: 'coverage', label: 'Sort: coverage %' },
                { value: 'endpoints', label: 'Sort: endpoint count' },
                { value: 'label', label: 'Sort: A–Z' },
              ]}
              value={sortBy}
              onChange={(v) => setSortBy((v as SortKey) || 'coverage')}
              placeholder="Sort"
              searchPlaceholder="Search…"
              compact
            />
          </div>
        </div>
      </div>
      {bulkResult && (
        <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-100 text-xs text-emerald-700">
          {bulkResult}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-100">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Module</th>
              <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wide">Tools</th>
              <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wide">Endpoints</th>
              <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Coverage</th>
              <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wide">Score</th>
              <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wide">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {sorted.map((m) => (
              <ModuleRow
                key={m.id}
                module={m}
                isBulkStubbing={bulkStubbing === m.id}
                onBulkStub={() => setPendingBulk(m)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={!!pendingBulk}
        title="Create stubs for every uncovered GET endpoint?"
        tone="emerald"
        confirmLabel="Create stubs"
        busy={!!bulkStubbing}
        onCancel={() => setPendingBulk(null)}
        onConfirm={() => pendingBulk && void runBulkStub(pendingBulk)}
        message={
          pendingBulk
            ? `This creates a draft tool row (coverage=stub) for each uncovered GET endpoint in ${pendingBulk.domains.join(', ')} — module "${pendingBulk.label}". You can review and promote them afterwards.`
            : ''
        }
      />
    </div>
  );
};

export default ModuleCoverageMatrix;
